import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { message } from "antd";
import Layout from "../../../Layout";
import HeaderBread from "../../../components/HeaderBread";
import CustomizedDragNDrop from "../../../components/UI/CustomizedDragNDrop";
import ActiveSwitchFile from "../../../components/SwitchFile";
import DeletePop from "../../../constants/ErrorPermissionModal";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import { checkPermission } from "../../../utils/Validation/checkPermission";
import { addlog } from "../../../utils/ApiCalls/Logs";
import {
  getMenubarDataById,
  deleteMenubarSubmenu,
  updateMenubarSubmenu,
  updateMenubarSubmenuPosition,
} from "../../../utils/ApiCalls/Menubar";

const MODULE_NAME = "Menubar";

const MenubarMenuItems = () => {
  const params = useParams();
  const menubarId = params?.menubarId;
  const navigate = useNavigate();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();
  const [menubar, setMenubar] = useState({});
  const [rows, setRows] = useState([]);

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Menubar", route: "/website/cms/homepage/menubar" },
    { title: "Sub-menu", route: `/website/cms/homepage/menubar/${menubarId}/view` },
  ];

  const getData = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    let data = await getMenubarDataById(menubarId);
    if (data?.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      setMenubar(data);
      setRows(data?.menus || []);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menubarId]);

  const handleUpdate = async (id, values, title) => {
    if (!checkPermission(permission, "update_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }
    let data = await updateMenubarSubmenu(menubarId, id, values);
    if (data?.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      addlog("Menubar Sub-menu Updated", MODULE_NAME, { id, title, ...values }, userDetails.id);
    }
    await getData();
  };

  const handleEdit = (id) => {
    if (!checkPermission(permission, "update_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }
    navigate(`/website/cms/homepage/menubar/${menubarId}/view/edit/${id}`);
  };

  const handleDelete = async (id, title) => {
    if (!checkPermission(permission, "delete_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Missing Permission for delete Sub-menu");
      return;
    }
    await deleteMenubarSubmenu(menubarId, id);
    addlog("Menubar Sub-menu Deleted", MODULE_NAME, { id, title, menubarId }, userDetails.id);
    await getData();
  };

  const updateSubmenuPositionHandler = async (updatedRows) => {
    if (!checkPermission(permission, "update_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }
    dispatch({ type: "SET_LOADING", loading: true });
    // CustomizedDragNDrop computes row.position for a descending-sorted list
    // (top row = highest number); this list is sorted ascending by order,
    // so derive the value from each row's new index instead of using it as-is.
    const items = updatedRows.map((row, index) => ({ id: row.id, order: index + 1 }));
    let data = await updateMenubarSubmenuPosition(menubarId, items);
    if (data?.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      addlog("Menubar Sub-menu Position Updated", MODULE_NAME, { menubarId }, userDetails.id);
    }
    await getData();
  };

  const columns = [
    {
      id: "id",
      title: "Sl",
      width: 30,
      dataIndex: "id",
      key: "id",
      render: (text, record, index) => index + 1,
    },
    {
      id: "menuName",
      title: "Menu Name",
      dataIndex: "menuName",
      key: "menuName",
      width: 150,
    },
    {
      id: "url",
      title: "URL / Slug",
      dataIndex: "url",
      key: "url",
      width: 200,
    },
    {
      id: "is_active",
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (item, record, index) => (
        <ActiveSwitchFile
          module={MODULE_NAME}
          item={item}
          record={record}
          index={index}
          handleUpdate={(id, values, title) => handleUpdate(id, values, title)}
        />
      ),
    },
    {
      id: "operation",
      title: "Action",
      key: "operation",
      width: 100,
      render: (text, record) => (
        <div className="table_action_btn">
          <button className="btn" onClick={() => handleEdit(record.id)}>
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit"></i>
          </button>
          <DeletePop onConfirm={() => handleDelete(record.id, record.menuName)}>
            <div>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" data-toggle="tooltip" title="Delete"></i>
              </button>
            </div>
          </DeletePop>
        </div>
      ),
    },
  ];

  return (
    <Layout type={"read_per"} module_data={MODULE_NAME}>
      <div>
        <HeaderBread
          navigation={navigation}
          title={`Sub-menu — ${menubar?.menuName || ""}`}
        ></HeaderBread>
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Sub-menu List</h5>
                <Link
                  to={`/website/cms/homepage/menubar/${menubarId}/view/add`}
                  className="btn add_v_btn"
                >
                  Add Sub-menu
                </Link>
              </div>
              <div className="card-body">
                <div className="table_holder">
                  {rows && (
                    <CustomizedDragNDrop
                      setRows={setRows}
                      setPageSize={() => {}}
                      setPage={() => {}}
                      pageSize={Math.max(rows.length, 10)}
                      page={1}
                      count={rows.length}
                      columns={columns}
                      rows={rows}
                      sortHandler={() => {}}
                      updatePosition={updateSubmenuPositionHandler}
                      hidePagination
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default MenubarMenuItems;
